import type PlausibleFactory from 'plausible-tracker';
import type TagManager from 'react-gtm-module';
import type { MatomoTracker as Matomo } from '@canvasflow/analytics';

import { Logger } from '@shared/lib/Logger';

export interface Tracker {
	sendEvent: (event: TrackingEvent) => void;
}

/**
 * The opaque "is this tracker ready" handles `Tracking.tsx` keeps in state
 * and threads through `TrackingContext`/`Trackers` -- one field per
 * initialized (or not-yet-initialized, hence the `| null`) tracker. Each
 * type is the real thing the corresponding `initializeX` function passes to
 * its `setX` state setter (see `Tracking.tsx`), not a guess: `gtag` is a
 * readiness boolean (there's nothing else to store -- the real `gtag`
 * function lives on `window.gtag`), `gtm` is the whole `react-gtm-module`
 * export, `plausible` is the tracker instance `Plausible()` returns,
 * `metaPixel`/`tiktokPixel`/`segment` are the pixel/write-key strings the
 * respective tracker classes take in their constructors, and `matomo` is a
 * real `MatomoTracker` instance.
 */
export interface TrackerHandles {
	gtag?: boolean | null;
	gtm?: typeof TagManager | null;
	plausible?: ReturnType<typeof PlausibleFactory> | null;
	metaPixel?: string | null;
	tiktokPixel?: string | null;
	segment?: string | null;
	matomo?: Matomo | null;
}

export const TrackerLogger = new Logger('Tracking', {
	log: 'rgb(255, 149, 0)',
	debug: 'rgb(255, 149, 0)',
	info: 'rgb(255, 212, 38)',
	error: 'rgb(201, 52, 0)',
});

export type TrackingEvent =
	| InstalledEvent
	| LoginEvent
	| OpenTocEvent
	| CloseTocEvent
	| ShareEvent
	| LanguageOnChangeEvent
	| ButtonClickInstallEvent
	| ButtonClickLogInEvent
	| ButtonClickLogOutEvent
	| ButtonClickResponsiveEvent
	| ButtonClickReplicaEvent
	| ButtonClickSingleEvent
	| ButtonClickDoubleEvent
	| ButtonClickFavoriteArticleEvent
	| ButtonClickUnfavoriteArticleEvent
	| ButtonClickIssueEvent;

type EventType =
	| 'INSTALLED'
	| 'LOGIN'
	| 'OPEN_TOC'
	| 'CLOSE_TOC'
	| 'SHARE'
	| 'LANGUAGE_CHANGE'
	| 'BTN_CLICK_INSTALL'
	| 'BTN_CLICK_LOGIN'
	| 'BTN_CLICK_LOGOUT'
	| 'BTN_CLICK_RESPONSIVE'
	| 'BTN_CLICK_REPLICA'
	| 'BTN_CLICK_REPLICA_SINGLE'
	| 'BTN_CLICK_REPLICA_DOUBLE'
	| 'BTN_CLICK_FAVORITE_ARTICLE'
	| 'BTN_CLICK_UNFAVORITE_ARTICLE'
	| 'BTN_CLICK_ISSUE';

interface BaseEvent {
	type: EventType;
	location?: string;
}

interface InstalledEvent extends BaseEvent {
	type: 'INSTALLED';
}

interface LoginEvent extends BaseEvent {
	type: 'LOGIN';
}

interface OpenTocEvent extends BaseEvent {
	type: 'OPEN_TOC';
}

interface CloseTocEvent extends BaseEvent {
	type: 'CLOSE_TOC';
}

interface ShareEvent extends BaseEvent {
	type: 'SHARE';
	data: TrackingData;
}

export interface ButtonClickInstallEvent extends BaseEvent {
	type: 'BTN_CLICK_INSTALL';
}

export interface ButtonClickLogInEvent extends BaseEvent {
	type: 'BTN_CLICK_LOGIN';
}

interface ButtonClickResponsiveEvent extends BaseEvent {
	type: 'BTN_CLICK_RESPONSIVE';
	data: TrackingData;
}

interface ButtonClickReplicaEvent extends BaseEvent {
	type: 'BTN_CLICK_REPLICA';
	data: TrackingData;
}

interface ButtonClickSingleEvent extends BaseEvent {
	type: 'BTN_CLICK_REPLICA_SINGLE';
	data: TrackingData;
}

interface ButtonClickDoubleEvent extends BaseEvent {
	type: 'BTN_CLICK_REPLICA_DOUBLE';
	data: TrackingData;
}

interface ButtonClickFavoriteArticleEvent extends BaseEvent {
	type: 'BTN_CLICK_FAVORITE_ARTICLE';
	data: TrackingData;
}

interface ButtonClickUnfavoriteArticleEvent extends BaseEvent {
	type: 'BTN_CLICK_UNFAVORITE_ARTICLE';
	data: TrackingData;
}

interface ButtonClickLogOutEvent extends BaseEvent {
	type: 'BTN_CLICK_LOGOUT';
}

interface ButtonClickIssueEvent extends BaseEvent {
	type: 'BTN_CLICK_ISSUE';
	data: TrackingData;
}

interface LanguageOnChangeEvent extends BaseEvent {
	type: 'LANGUAGE_CHANGE';
	data: TrackingData;
}

export interface TrackingData {
	lang?: string;
	method?: 'clipboard' | 'os';
	contentType?: 'article' | 'issue';
	issue?: {
		id?: number;
		title?: string;
		slug?: string;
	};
	article?: {
		id?: number;
		ArticleID?: number;
		slug?: string;
		title?: string;
	};
}
